import type { Ref } from "vue";

import type { GenericModel } from "./model";

export type Selection<T> =
  | { mode: "some"; items: [T, ...T[]] }
  | { mode: "all_except"; items: T[] };

export const isRowSelected = <T extends GenericModel>(
  selection: Selection<T> | undefined,
  item: T
): boolean => {
  if (!selection) {
    return false;
  }

  switch (selection.mode) {
    case "all_except": {
      return !selection.items.some((i) => i.id === item.id);
    }
    case "some": {
      return selection.items.some((i) => i.id === item.id);
    }
  }
};

export const toggleRowSelection = <T extends GenericModel>(
  selection: Ref<Selection<T> | undefined> | undefined,
  item: T,
  next: boolean
) => {
  if (!selection) {
    return;
  }

  const current = selection.value;

  if (!current || current.mode === "some") {
    if (next) {
      const items = current
        ? [...current.items.filter((i) => i.id !== item.id), item]
        : [item];

      selection.value = {
        mode: "some",
        items: items as [T, ...T[]],
      };
    } else if (current) {
      const items = current.items.filter((i) => i.id !== item.id);

      if (items.length === 0) {
        selection.value = undefined;
        return;
      }

      // we've asserted above that items has at least one item
      selection.value = {
        mode: "some",
        items: items as [T, ...T[]],
      };
    }
  } else if (current.mode === "all_except") {
    const items = current.items.filter((i) => i.id !== item.id);

    selection.value = {
      mode: "all_except",
      items: next ? items : [...items, item],
    };
  }
};
